/**
 * ChapterEditor — 7957-A simplified
 *
 * No longer a full panel with SuggestionTypes and Header.
 * Now a headless component that:
 * 1. Watches feedbackPending in the store to start AI feedback
 * 2. Manages streaming, progress, and cancel logic
 * 3. Renders a compact FeedbackPanel (progress bar + navigation) when loading or when results exist
 * 4. Locks the editor during feedback generation
 *
 * The SuggestionTypes grid and mode selector have moved to FeedbackTypeSelector dropdown.
 * The Header (close button + model selector) has been removed — the toolbar tab is the entry point.
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { themeVariables } from "src/themeVariables";
import { useProjectStore } from "src/stores/projectStore";
import { useCommentStore } from "../../../stores/commentStore";
import Auth from "src/Auth";
import { useToggleCommentMode } from "src/hooks/useToggleCommentMode";
import { useToast } from "../../ToastWrapper";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { TourSignal } from "../../StickyTour/TourTypes";
import { getFeedbackCommentSidebarVisibilityOnStart } from "../feedbackSidebarVisibility";
import { getProjectFeedbackCategoryState } from "../feedbackCategorySelection";
import { getFeedbackSelectionPayload } from "../feedbackRequestPayload";
import useEditPluginInstallations from "../../../hooks/useEditPluginInstallations";
import { getFeedbackToastMessage } from "./feedbackToast";
import { isOffScreenFeedbackTarget } from "./feedbackTargeting";

const AGENTIC_FEEDBACK_FAILURE_MESSAGE = "Feedback generation failed. Please try again.";

async function reportAgenticFeedbackFailureToChat(triggerSource) {
  if (triggerSource !== "agentic_chat") return;
  const { reportAgenticFeedbackRunFailed } = await import("src/stores/persistentChatStore");
  reportAgenticFeedbackRunFailed(AGENTIC_FEEDBACK_FAILURE_MESSAGE);
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FeedbackLine = styled.p`
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.$delay}s;
  line-height: 1.5;
  margin: 0;
  padding: 2px 0;
`;

const FeedbackBox = styled.div`
  background-color: ${themeVariables.menuColor};
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  color: ${themeVariables.grey0};

  h4 {
    font-weight: 500;
    margin-bottom: 8px;
    opacity: 0;
    animation: ${fadeIn} 0.5s ease-out forwards;
  }
`;

const FeedbackPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 8px 15px 8px 20px;
  background-color: ${themeVariables.menuColorFullscreen};
  border-radius: 12px;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
`;

const ChapterEditorContainer = styled.div`
  position: relative;
  color: ${themeVariables.grey0};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

function AnimatedFeedback({ text }) {
  const lines = text.split("\n").filter((line) => line.trim() !== "");

  return (
    <FeedbackBox>
      <h2>Overall Feedback</h2>
      {lines.map((line, index) => (
        <FeedbackLine
          key={index}
          $delay={0.2 + index * 0.15}
        >
          {line}
        </FeedbackLine>
      ))}
    </FeedbackBox>
  );
}

function ChapterEditor({ centerColumnRef, editorToolbarRef, editorRef, editorEndRef, editor, projectId, documentId, editableDocumentTitleRef }) {
  const chapterEditorActive = useStoryBibleStore((state) => state.chapterEditorActive);
  const setChapterEditorActive = useStoryBibleStore((state) => state.setChapterEditorActive);
  const feedbackRequest = useStoryBibleStore((state) => state.feedbackRequest);
  const feedbackPending = useStoryBibleStore((state) => state.feedbackPending);
  const feedbackTriggerSource = useStoryBibleStore((state) => state.feedbackTriggerSource);
  const clearFeedbackPending = useStoryBibleStore((state) => state.clearFeedbackPending);
  const feedbackModel = useStoryBibleStore((state) => state.feedbackModel);
  const selectedFeedbackCategoryGlobal = useStoryBibleStore((state) => state.selectedFeedbackCategory);
  const hasUserSelectedFeedbackCategoryGlobal = useStoryBibleStore((state) => state.hasUserSelectedFeedbackCategory);
  const feedbackCategoryByProjectId = useStoryBibleStore((state) => state.feedbackCategoryByProjectId);
  const project = useProjectStore((state) => state.project);
  const agenticChatFeedbackRunRequest = useStoryBibleStore((state) => state.agenticChatFeedbackRunRequest);
  const consumeAgenticChatFeedbackRunRequest = useStoryBibleStore((state) => state.consumeAgenticChatFeedbackRunRequest);
  const triggerFeedback = useStoryBibleStore((state) => state.triggerFeedback);
  const feedbackAbortController = useStoryBibleStore((state) => state.feedbackAbortController);
  const setFeedbackAbortController = useStoryBibleStore((state) => state.setFeedbackAbortController);
  const abortFeedback = useStoryBibleStore((state) => state.abortFeedback);
  const { selectedFeedbackCategory } = getProjectFeedbackCategoryState({
    projectId,
    projectFeedbackPreferences: project?.data?.feedbackPreferences,
    feedbackCategoryByProjectId,
    selectedFeedbackCategory: selectedFeedbackCategoryGlobal,
    hasUserSelectedFeedbackCategory: hasUserSelectedFeedbackCategoryGlobal,
  });
  const { installations: feedbackInstallations } = useEditPluginInstallations();
  const feedbackSelectionRef = useRef(null);
  feedbackSelectionRef.current = {
    installations: feedbackInstallations,
    feedbackPreferences: project?.data?.feedbackPreferences,
    selectedFeedbackCategory,
  };
  /** When set before Feedback starts, restricts run to exact type names (agent multi-select path). Consumed once per request. */
  const pendingFeedbackTypeNamesRef = useRef(undefined);
  /** Resolved off-screen document target for the next agent-triggered Feedback run. */
  const pendingFeedbackDocumentIdRef = useRef(undefined);
  const pendingFeedbackRunIdRef = useRef(undefined);
  const offScreenFeedbackRunRef = useRef(false);
  const activeFeedbackTriggerSourceRef = useRef(null);
  const document = useProjectStore((state) => state.document);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState("reading");
  const [loadingPercentage, setLoadingPercentage] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [count, setCount] = useState(0);
  const [overall, setOverall] = useState("");
  const [isSticky, setIsSticky] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [cancelAvailable, setCancelAvailable] = useState(false);
  const [creditsUsed, setCreditsUsed] = useState(null);
  const [totalComments, setTotalComments] = useState(null);

  const comments = useCommentStore((state) => state.comments);
  const setWaitingForFeedbackComments = useCommentStore((state) => state.setWaitingForFeedbackComments);
  const { showComments, toggleComments } = useToggleCommentMode();
  const { setToast } = useToast();
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const sendFailureSignal = useStickyTourStore((state) => state.sendFailureSignal);

  // Refs for sticky behavior
  const feedbackPanelRef = useRef(null);
  const directControlRef = useRef(false);
  const bottomScrollingRef = useRef(false);
  const mainContainerRef = useRef(null);

  // Stores the LLM's explanation when it decides no comments are needed
  const noCommentsMessageRef = useRef(null);
  // Stores pending toast message to fire in onDone (after state changes)
  const pendingToastRef = useRef(null);

  // Optimized scroll handling with sticky positioning
  useEffect(() => {
    const handleScroll = () => {
      if (!editorToolbarRef?.current || !feedbackPanelRef.current || !centerColumnRef?.current || !mainContainerRef.current || !editorEndRef?.current || !editorRef?.current) return;

      const toolbarRect = editorToolbarRef.current.getBoundingClientRect();
      const panelRect = feedbackPanelRef.current.getBoundingClientRect();
      const mainContainerRect = mainContainerRef.current.getBoundingClientRect();
      const editorEndRect = editorEndRef.current.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();

      // Sticky positioning logic
      if (mainContainerRect.bottom - panelRect.height < toolbarRect.bottom && !directControlRef.current) {
        directControlRef.current = true;
        setIsSticky(true);
        feedbackPanelRef.current.style.position = "fixed";
        feedbackPanelRef.current.style.top = `${toolbarRect.bottom}px`;
        feedbackPanelRef.current.style.width = `${mainContainerRect.width}px`;
        feedbackPanelRef.current.style.left = `${mainContainerRect.left}px`;
        feedbackPanelRef.current.style.marginLeft = "0";
        feedbackPanelRef.current.style.marginRight = "0";
        feedbackPanelRef.current.style.boxShadow = themeVariables.dropShadow1525;
        feedbackPanelRef.current.style.borderTop = `1px solid ${themeVariables.divider}`;
        feedbackPanelRef.current.style.borderTopLeftRadius = "0";
        feedbackPanelRef.current.style.borderTopRightRadius = "0";
        feedbackPanelRef.current.style.zIndex = "10";
        return;
      }

      const toolbarEditorEndOffset = 50;

      if (editorEndRect.top - toolbarEditorEndOffset <= toolbarRect.bottom && directControlRef.current && !bottomScrollingRef.current) {
        const mainContainerHeight = mainContainerRect.bottom - mainContainerRect.top;
        const editorHeight = editorRect.bottom - editorRect.top;
        const panelHeight = panelRect.bottom - panelRect.top;
        const vStackPadding = 24;
        const totalOffset = mainContainerHeight + editorHeight - panelHeight - vStackPadding;

        feedbackPanelRef.current.style.position = "absolute";
        feedbackPanelRef.current.style.top = `${totalOffset}px`;
        feedbackPanelRef.current.style.left = "0";
        feedbackPanelRef.current.style.right = "0";
        feedbackPanelRef.current.style.width = "100%";
        feedbackPanelRef.current.style.marginLeft = "";
        feedbackPanelRef.current.style.marginRight = "";
        feedbackPanelRef.current.style.boxShadow = themeVariables.dropShadow1525;
        feedbackPanelRef.current.style.borderTop = `1px solid ${themeVariables.divider}`;
        feedbackPanelRef.current.style.zIndex = "10";
        bottomScrollingRef.current = true;
        return;
      }

      if (editorEndRect.top - toolbarEditorEndOffset > toolbarRect.bottom && directControlRef.current && bottomScrollingRef.current) {
        feedbackPanelRef.current.style.position = "fixed";
        feedbackPanelRef.current.style.top = `${toolbarRect.bottom}px`;
        feedbackPanelRef.current.style.width = `${mainContainerRect.width}px`;
        feedbackPanelRef.current.style.left = `${mainContainerRect.left}px`;
        feedbackPanelRef.current.style.marginLeft = "0";
        feedbackPanelRef.current.style.marginRight = "0";
        feedbackPanelRef.current.style.boxShadow = themeVariables.dropShadow1525;
        feedbackPanelRef.current.style.borderTop = `1px solid ${themeVariables.divider}`;
        feedbackPanelRef.current.style.zIndex = "10";
        bottomScrollingRef.current = false;
        return;
      }

      if (mainContainerRect.bottom - panelRect.height >= toolbarRect.bottom && directControlRef.current) {
        directControlRef.current = false;
        setIsSticky(false);
        feedbackPanelRef.current.style.position = "";
        feedbackPanelRef.current.style.top = "";
        feedbackPanelRef.current.style.width = "";
        feedbackPanelRef.current.style.left = "";
        feedbackPanelRef.current.style.right = "";
        feedbackPanelRef.current.style.marginLeft = "";
        feedbackPanelRef.current.style.marginRight = "";
        feedbackPanelRef.current.style.boxShadow = "";
        feedbackPanelRef.current.style.borderTop = "";
        feedbackPanelRef.current.style.borderTopLeftRadius = "";
        feedbackPanelRef.current.style.borderTopRightRadius = "";
        feedbackPanelRef.current.style.zIndex = "";
      }
    };

    const centerColumn = centerColumnRef?.current;
    if (centerColumn) {
      centerColumn.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        centerColumn.removeEventListener("scroll", handleScroll);
      };
    }
  }, [editorToolbarRef, feedbackPanelRef, centerColumnRef, mainContainerRef, editorEndRef, editorRef]);

  // Handle width updates when in fixed position
  useEffect(() => {
    const handleResize = () => {
      if (!feedbackPanelRef.current || !mainContainerRef.current || !directControlRef.current) return;

      const mainContainerRect = mainContainerRef.current.getBoundingClientRect();
      feedbackPanelRef.current.style.width = `${mainContainerRect.width}px`;
      feedbackPanelRef.current.style.left = `${mainContainerRect.left}px`;
    };

    const centerColumn = centerColumnRef?.current;
    if (centerColumn) {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(centerColumn);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [centerColumnRef, feedbackPanelRef, mainContainerRef, directControlRef]);

  // Timer to make cancel available after 5 seconds
  useEffect(() => {
    let timer;
    if (isLoading) {
      setCancelAvailable(false);
      timer = setTimeout(() => {
        setCancelAvailable(true);
      }, 5000);
    } else {
      setCancelAvailable(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    // Reset percentage when loading stops
    if (!isLoading) {
      setLoadingPercentage(0);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!agenticChatFeedbackRunRequest?.id) return;
    const requestId = agenticChatFeedbackRunRequest.id;
    const names = agenticChatFeedbackRunRequest.feedbackTypeNames;
    pendingFeedbackRunIdRef.current = agenticChatFeedbackRunRequest.feedbackRunId;
    pendingFeedbackDocumentIdRef.current = agenticChatFeedbackRunRequest.documentId || undefined;
    offScreenFeedbackRunRef.current = isOffScreenFeedbackTarget(
      agenticChatFeedbackRunRequest.documentId,
      document.id,
    );
    if (names == null) {
      pendingFeedbackTypeNamesRef.current = null;
    } else if (names.length === 0) {
      pendingFeedbackTypeNamesRef.current = [];
    } else {
      pendingFeedbackTypeNamesRef.current = names;
    }
    consumeAgenticChatFeedbackRunRequest(requestId);
    if (!offScreenFeedbackRunRef.current) {
      setChapterEditorActive(true);
    }
    triggerFeedback("agentic_chat");
  }, [
    agenticChatFeedbackRunRequest?.id,
    agenticChatFeedbackRunRequest?.feedbackTypeNames,
    agenticChatFeedbackRunRequest?.documentId,
    document.id,
    consumeAgenticChatFeedbackRunRequest,
    setChapterEditorActive,
    triggerFeedback,
  ]);

  // If this instance unmounts after the agentic request was consumed and
  // triggerFeedback("agentic_chat") queued a run but before handleGetFeedback
  // cleared feedbackPending, drop the handoff on unmount. Remount should not
  // auto-start feedback (user may have left or changed their mind). Button
  // triggers keep feedbackPending so Stop → re-click still works.
  useEffect(() => {
    return () => {
      const { feedbackPending, feedbackTriggerSource, clearFeedbackPending } = useStoryBibleStore.getState();
      if (feedbackPending && feedbackTriggerSource === "agentic_chat") {
        clearFeedbackPending();
      }
    };
  }, []);

  // Reset loading state when abort comes from the toolbar Stop button.
  // The existing wasAborted error path skips cleanup, so this useEffect
  // detects when the controller is cleared externally and resets local state.
  useEffect(() => {
    if (!feedbackAbortController && isLoading) {
      setIsLoading(false);
      setLoadingPercentage(0);
      setWaitingForFeedbackComments(false);
      setChapterEditorActive(false);
    }
  }, [feedbackAbortController]);

  const handleGetFeedback = useCallback(
    async (e, { fromTrigger = false, triggerSource = null, quotedRequest = null } = {}) => {
      if (!fromTrigger) offScreenFeedbackRunRef.current = false;
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // If currently loading and invoked by a button click (not trigger):
      // only cancel if cancel is available, then return.
      if (isLoading && feedbackAbortController && !fromTrigger) {
        if (cancelAvailable) {
          abortFeedback();
        }
        return;
      }

      setErrorMessage("");
      setIsLoading(true);
      setLoadingPhase("reading");
      setCount(0);
      setOverall("");
      setCreditsUsed(null);
      setTotalComments(null);
      noCommentsMessageRef.current = null;
      pendingToastRef.current = null;

      // Create new abort controller for this request
      const newAbortController = new AbortController();
      setFeedbackAbortController(newAbortController);
      const ownsRun = () => !newAbortController.signal.aborted
        && useStoryBibleStore.getState().feedbackAbortController === newAbortController;
      const resolvedTriggerSource = fromTrigger
        ? (triggerSource || "unknown")
        : "button";
      activeFeedbackTriggerSourceRef.current = resolvedTriggerSource;

      // The request outlives the document-keyed component. Keep its watchdog
      // alive across navigation and dispose it only when this request ends.
      let lastActivity = Date.now();
      const clearWatchdog = () => {
        clearInterval(watchdog);
        newAbortController.signal.removeEventListener("abort", clearWatchdog);
      };
      const watchdog = setInterval(() => {
        if (!ownsRun()) {
          clearWatchdog();
          return;
        }
        if (Date.now() - lastActivity < 5 * 60 * 1000) return;
        clearWatchdog();
        abortFeedback();
        setIsLoading(false);
        setLoadingPercentage(0);
        offScreenFeedbackRunRef.current = false;
        setErrorMessage("Feedback generation timed out. Please try again.");
        sendFailureSignal(TourSignal.FEEDBACK_FAILED);
        void reportAgenticFeedbackFailureToChat(resolvedTriggerSource);
        activeFeedbackTriggerSourceRef.current = null;
      }, 30 * 1000);
      newAbortController.signal.addEventListener("abort", clearWatchdog, { once: true });

      try {
        // The keyed editor prop identifies the active document; the project store can lag behind it.
        if (quotedRequest && quotedRequest.documentId !== documentId) throw new Error("Feedback document changed");
        const namesOverride = pendingFeedbackTypeNamesRef.current;
        pendingFeedbackTypeNamesRef.current = undefined;
        const documentIdOverride = pendingFeedbackDocumentIdRef.current;
        pendingFeedbackDocumentIdRef.current = undefined;
        const isOffScreenFeedback = isOffScreenFeedbackTarget(documentIdOverride, document.id);

        // Always set the guard flag so the auto-close useEffect in
        // EditorContainer (which watches `comments`) won't toggle the
        // sidebar off while feedback is in flight.  Previously this was
        // only set when `showComments` was false, leaving subsequent
        // runs unguarded — a race between onDone clearing the flag and
        // a late SWR revalidation could close the sidebar (~1-in-3).
        if (!isOffScreenFeedback) {
          setWaitingForFeedbackComments(true);
          const nextShowComments = getFeedbackCommentSidebarVisibilityOnStart({
            commentCount: comments.length,
            showComments,
          });
          if (nextShowComments !== showComments) {
            toggleComments(nextShowComments, { userInitiated: false });
          }
        }

        const feedbackRunId = pendingFeedbackRunIdRef.current;
        pendingFeedbackRunIdRef.current = undefined;
        const reqPayload = quotedRequest ? { ...quotedRequest, triggerSource: resolvedTriggerSource } : {
          ...(feedbackRunId ? { feedbackRunId } : {}),
          documentId: documentIdOverride || document.id,
          model: feedbackModel,
          triggerSource: resolvedTriggerSource,
          ...getFeedbackSelectionPayload({
            feedbackTypeNamesOverride: namesOverride,
            ...feedbackSelectionRef.current,
          }),
        };

        let savedFeedbackComments = 0;
        await Auth.streamWithIdToken(
          "/api/ai/editor",
          reqPayload,
          newAbortController.signal,
          (chunk) => {
            // Ignore chunks from an aborted or superseded request.
            if (!ownsRun()) return;
            lastActivity = Date.now();

            if (chunk.started) {
              setIsLoading(true);
              setLoadingPhase("suggesting");
            }

            if (chunk.count) {
              setCount(chunk.count);
            }

            if (chunk.overall && !isOffScreenFeedback) {
              setOverall(chunk.overall);
            }

            if (chunk.percentage !== undefined) {
              setLoadingPercentage(chunk.percentage);
            }

            if (chunk.creditsUsed !== undefined) {
              setCreditsUsed(chunk.creditsUsed);
            }

            if (chunk.totalComments !== undefined) {
              setTotalComments(chunk.totalComments);
            }

            if (chunk.noCommentsMessage) {
              noCommentsMessageRef.current = chunk.noCommentsMessage;
            }

            if (chunk.feedbackComplete) {
              savedFeedbackComments = chunk.savedComments ?? chunk.totalComments ?? 0;
              pendingToastRef.current = getFeedbackToastMessage(chunk, noCommentsMessageRef.current);
              noCommentsMessageRef.current = null;
            }

            if (chunk.aborted) {
              setIsLoading(false);
              setLoadingPercentage(0);
              setWaitingForFeedbackComments(false);
            }
          },
          () => {
            // onDone — ignore if this request was aborted (a new request owns state)
            if (!ownsRun()) return;
            clearWatchdog();
            setIsLoading(false);
            if (!isOffScreenFeedback) setChapterEditorActive(false);
            setLoadingPercentage(100);
            setFeedbackAbortController(null);
            setWaitingForFeedbackComments(false);
            // Completion can precede both comment fetching and manuscript sync.
            // Open explicitly for successful results instead of relying on the
            // first-comment effect, whose waiting flag has just been cleared.
            if (!isOffScreenFeedback && savedFeedbackComments > 0
              && useCommentStore.getState().documentId === reqPayload.documentId) {
              toggleComments(true, { userInitiated: false });
            }
            offScreenFeedbackRunRef.current = false;
            activeFeedbackTriggerSourceRef.current = null;
            sendFlowSignal(TourSignal.FEEDBACK_COMPLETED);

            // Safety net: force a final comment refetch after feedback completes.
            // This catches any comments that were created but whose WebSocket signals
            // arrived within SWR's 2-second deduplication window.
            if (!isOffScreenFeedback) {
              setTimeout(() => {
                if (newAbortController.signal.aborted
                  || useCommentStore.getState().documentId !== reqPayload.documentId) return;
                const mutateComments = useCommentStore.getState().mutateComments;
                if (mutateComments) {
                  mutateComments();
                }
              }, 500);
            }

            // Fire toast after state changes complete. In React 17, setState calls
            // in async contexts (promise callbacks) are not batched, so the guard at
            // the top of this component may return null before setToast runs.
            // setTimeout(0) defers the toast to after React finishes re-rendering.
            if (pendingToastRef.current) {
              const msg = pendingToastRef.current;
              pendingToastRef.current = null;
              setTimeout(() => setToast(msg), 0);
            }
          },
          (error) => {
            if (!ownsRun()) return;
            // If this error is from an aborted request (e.g., we force-aborted a stale
            // request to start a new one), bail out without touching state — the new
            // request owns the loading/abort state now.
            const wasAborted =
              error.name === "AbortError" ||
              error.code === 20 ||
              error.message?.includes("aborted") ||
              error.message?.includes("cancelled") ||
              newAbortController.signal.aborted;

            if (wasAborted) {
              return;
            }

            // onError — only reset state for non-abort errors
            clearWatchdog();
            setIsLoading(false);
            if (!isOffScreenFeedback) setChapterEditorActive(false);
            setLoadingPercentage(0);
            setFeedbackAbortController(null);
            setWaitingForFeedbackComments(false);
            offScreenFeedbackRunRef.current = false;
            sendFailureSignal(TourSignal.FEEDBACK_FAILED);
            // Fire toast last — ChapterEditor may return null after state changes above
            void reportAgenticFeedbackFailureToChat(resolvedTriggerSource);
            activeFeedbackTriggerSourceRef.current = null;
            setTimeout(() => setToast(AGENTIC_FEEDBACK_FAILURE_MESSAGE), 0);

            // Handle backend validation errors
            if (error.response) {
              error.response
                .json()
                .then((data) => {
                  setErrorMessage(data.error || "An error occurred");
                })
                .catch(() => {
                  setErrorMessage("An error occurred");
                });
            } else {
              setErrorMessage(error.message || "An error occurred");
            }
          }
        );
      } catch (error) {
        if (!ownsRun()) return;
        // If this error is from an aborted request, don't reset state
        const wasAborted =
          error.name === "AbortError" ||
          error.code === 20 ||
          error.message?.includes("aborted") ||
          error.message?.includes("cancelled") ||
          newAbortController.signal.aborted;

        if (wasAborted) {
          return;
        }

        setIsLoading(false);
        clearWatchdog();
        // A failed approved handoff must release the toolbar state set by Continue.
        if (quotedRequest) setChapterEditorActive(false);
        setLoadingPercentage(0);
        setFeedbackAbortController(null);
        setWaitingForFeedbackComments(false);
        offScreenFeedbackRunRef.current = false;
        setErrorMessage("Failed to start feedback generation");
        sendFailureSignal(TourSignal.FEEDBACK_FAILED);
        void reportAgenticFeedbackFailureToChat(resolvedTriggerSource);
        activeFeedbackTriggerSourceRef.current = null;
        setTimeout(() => setToast(AGENTIC_FEEDBACK_FAILURE_MESSAGE), 0);
      }
    },
    [documentId, document.id, feedbackModel, toggleComments, setWaitingForFeedbackComments, isLoading, feedbackAbortController, cancelAvailable, showComments, comments.length, abortFeedback, setFeedbackAbortController, setToast, sendFlowSignal, sendFailureSignal]
  );

  // Watch feedbackPending — when true, start analysis and clear the flag.
  // Unlike the previous ref-based feedbackTriggerCount mechanism, this
  // survives component unmount/remount because the flag lives in the store.
  // This fixes the bug where Stop → re-click lost the trigger due to
  // ChapterEditor unmounting (when overall was empty) and remounting with
  // prevTriggerCountRef already matching the incremented count.
  useEffect(() => {
    if (feedbackPending) {
      const triggerSourceForRun = feedbackTriggerSource;
      clearFeedbackPending();
      handleGetFeedback(null, { fromTrigger: true, triggerSource: triggerSourceForRun, quotedRequest: feedbackRequest });
    }
  }, [feedbackPending, feedbackTriggerSource, feedbackRequest, clearFeedbackPending, handleGetFeedback]);

  // Don't render anything if feedback is not active AND not loading AND no results
  if (offScreenFeedbackRunRef.current) return null;
  if (!chapterEditorActive && !isLoading && !overall) return null;

  const getButtonText = () => {
    if (!isLoading) return "Feedback";
    if (isButtonHovered && cancelAvailable) return "Cancel";
    return loadingPhase === "reading" ? "Thinking..." : `Suggesting${count !== 0 ? ` (${count})` : "..."}`;
  };

  return (
    <ChapterEditorContainer ref={mainContainerRef}>
      {overall && <AnimatedFeedback text={overall} />}
    </ChapterEditorContainer>
  );
}

export default ChapterEditor;
